import React, { useState, useEffect, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import LoadingContext from '../../context/Loading/Loading'
import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import EventDetailsContext from '../../context/EventDetails/EventDetails'

import { getEvent, removeOffer } from '../../utilities/api'

import useSafeAsync from '../../utilities/useSafeAsync'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import {
  checkPermission,
  getNowInTimezone,
  getTimezoneDate,
  globalStatuses,
} from '../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'

import { EmptyContainer } from '../EmptyContainer'
import { CreateOfferWrapper } from './CreateOfferWrapper'
import {
  heldOrKilledSeatIds,
  isOfferSoldOut,
  openInventoryForEvent,
  remainingSeatCount,
} from './offerAvailability'
import { Offers } from './Offers'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { CreateOfferModal } from './CreateOfferModal'
import { UpdatingAlert } from '../UpdatingAlert'

export default function OffersWrapper({ eventId, id }) {
  const safeSetState = useSafeAsync()

  const navigate = useNavigate()
  const location = useLocation()

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const { orgPermissions, user } = useContext(UserContext)

  const {
    isGAOnlyEvent,
    updateEvent,
    updateCanPublish,
    standardAdmissionOfferHasInventory,
    updateIsEventOnsale,
    updateIsEventPublishing,
    updateIsEventPublished,
    updateHasEventEnded,
    updateIsEventSoldout,
    setIsGAOnlyEvent,
    isEventPublishing,
    isEventPublished,
    isEventOnsale,
    hasEventEnded,
    isEventSoldout,
  } = useContext(EventDetailsContext)

  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT
    )

  const [event, setEvent] = useState()

  const [openInventory, setOpenInventory] = useState()

  // offer type
  const [offerType, setOfferType] = useState()

  // all offers
  const [offers, setOffers] = useState()

  // all discounts
  const [discounts, setDiscounts] = useState(null)

  // current offer
  const [offer, setOffer] = useState()

  // flag to check whether changes to offer has been made or new offer has been created to check whether to fetch data again
  // to tell if back button was clicked
  const [isChangesMade, setIsChangesMade] = useState(false)

  const [isRemoving, setIsRemoving] = useState(false)

  // create offer modal
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    getData()
  }, [eventId, orgPermissionsReady, hasPermission])

  // on location change
  useEffect(() => {
    // viewing all offers
    if (
      !location.pathname.includes('edit') &&
      !location.pathname.includes('create')
    ) {
      setOffer()
      setOfferType()
      if (isChangesMade) {
        getData()
        setIsChangesMade(false)
      }
    }
  }, [location, isChangesMade])

  // get offer on refresh if editing
  useEffect(() => {
    if (location.pathname.includes('edit') && !offer) {
      const foundOffer =
        offers?.find((offer) => offer.id === Number(id)) ||
        offers
          ?.flatMap((offer) => offer.connected_offers ?? [])
          .find((offer) => offer.id === Number(id))
      setOffer(foundOffer)
    }
  }, [location, offers, offer])

  const getData = () => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    showLoading()
    getEvent(eventId)
      .then((res) => {
        safeSetState(() => {
          setEvent(res.data)
          setOffers(res.data?.offers)
          if (res?.data?.inventory)
            setOpenInventory(openInventoryForEvent(res?.data))
          // update context
          updateEvent(res?.data)
          setIsGAOnlyEvent(res?.data?.seatmap?.ga_only)
          updateCanPublish(res.data.status)
          updateIsEventOnsale(res?.data)
          updateHasEventEnded(res?.data)
          updateIsEventSoldout(res?.data)
          updateIsEventPublished(res?.data)
          updateIsEventPublishing(res?.data)
          hideLoading()
        })
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
  }

  // create offer modal
  const handleClose = () => setShow(false)

  // show CreateModal if event if GA only
  // else just go to create offer page
  const onClick = () => {
    if (isGAOnlyEvent || user?.user?.email === 'harrison@blocktickets.xyz') {
      setShow(true)
    } else {
      navigate('create')
    }
  }

  // handle different views
  const handleClick = (_, id, offer) => {
    if (!id) {
      navigate('create')
      setOffer()
    } else {
      setOffer(offer)
      navigate(`edit?id=${id}`)
    }
  }

  const handleCreate = (choice) => {
    setOfferType(choice)
    handleClose()
    handleClick()
  }

  // get offer status
  const getOfferStatus = (period, offer) => {
    if (period) {
      const { starts, ends } = period
      let key

      // ended - end time is same or before now - overrides any other status even hidden
      if (
        ends &&
        getTimezoneDate(ends, event?.timezone).isSameOrBefore(
          getNowInTimezone(event?.timezone)
        )
      ) {
        key = 'complete'
      } else {
        // offer is hidden
        if (offer?.isHidden) {
          key = 'hidden'
        }

        // scheduled if event is not published
        else if (!isEventPublished) {
          key = 'scheduled'

          // event is published
        } else {
          // TODO: hook up cancelled status - scheduled or onsale but offer dates have not passed ?

          const saleStarted =
            starts &&
            getTimezoneDate(starts, event?.timezone).isSameOrBefore(
              getNowInTimezone(event?.timezone)
            )
          const soldOut = isOfferSoldOut(offer, {
            openInventory,
            holds: event?.inventory?.holds,
            kills: event?.inventory?.kills,
            tickets: event?.tickets,
            saleStarted,
          })

          // Sold out only after inventory exists and nothing is left to sell.
          if (soldOut) {
            key = 'sold_out'
          }

          // onsale
          else if (saleStarted) {
            key = 'on_sale'
          }

          // scheduled
          else {
            key = 'scheduled'
          }
        }
      }
      // get status based on key
      return globalStatuses[key]
    }
  }

  // get all price level seats (regular and ga seats), includes holds/kills seats - event price levels
  // get all seats from offer (regular and ga seats), not including holds/kills seats - open inventory
  const getSeatCount = (obj) => {
    if (obj) {
      const seats = obj?.seats
      const gaSeats = Object.values(obj?.gaSeats)?.flatMap((seats) => seats)
      return [...seats, ...gaSeats]?.length
    }
  }

  const getAvailableSeatCount = (obj, options) =>
    remainingSeatCount(
      obj,
      event?.tickets,
      heldOrKilledSeatIds(event?.inventory?.holds, event?.inventory?.kills),
      options
    )

  // get availability periods of offer
  // all offers will have the availability periods of Standard Admission offer by default
  const getAvailabilityPeriods = (currentOffer) => {
    let availabilityPeriods = []

    if (offers && offers?.length === 0) return

    // get default general onsale periods
    if (offers) availabilityPeriods = offers[0]?.availability

    // existing offer
    // change the default periods to have the same start and end dates as periods in Standard Admission offer
    if (currentOffer) {
      // if offer is Standard Admission, just return its availability periods
      // change to compare offer[0] id to current offer id
      if (offers[0]?.id === currentOffer?.id) {
        return currentOffer?.availability
      }

      const currentOfferPeriods = currentOffer?.availability
      if (!currentOfferPeriods?.length) {
        return availabilityPeriods
      }
      const customAvailabilityPeriods = currentOfferPeriods?.filter(
        (period) => !period?.name?.includes('General On-sale')
      )
      const defaultAvailabilityPeriods = currentOfferPeriods?.filter((period) =>
        period?.name?.includes('General On-sale')
      )
      const updatedDefaultAvailabilityPeriods = defaultAvailabilityPeriods?.map(
        (period, idx) => {
          return {
            ...period,
            starts: availabilityPeriods[idx]?.starts,
            ends: availabilityPeriods[idx]?.ends,
          }
        }
      )
      availabilityPeriods = [
        ...updatedDefaultAvailabilityPeriods,
        ...(customAvailabilityPeriods?.length > 0
          ? customAvailabilityPeriods
          : []),
      ]
    }
    return availabilityPeriods
  }

  // update offer price depending on pricing option
  const getDiscountedPrice = (price, pricingOpt, discount) => {
    switch (pricingOpt) {
      // take away amount from price
      case 'decrease_by_price':
        return price - parseFloat(discount)

      // get percentage of price and take it away from price
      case 'decrease_by_percent':
        return Math.abs((parseFloat(discount) / 100) * price - price)

      // pricing option is custom price or match base price
      default:
        return price
    }
  }

  // handle applying discount to offer price if new price levels added or price is updated on price levels
  // if no discount, get offer price
  const getOfferPrice = (offerLevels, idx, pricingOption, discount) => {
    const offerPrice = offerLevels[idx]?.offerPrice?.toString()
    const basePrice = offerLevels[idx]?.basePrice?.toString()
    // if offer has discount
    if (discount) {
      // check if discount is already applied to offer price - only if discount price is the same as offer price
      if (
        getDiscountedPrice(basePrice, pricingOption, discount) == offerPrice
      ) {
        return offerPrice
      }
      // new price level or updated price
      else {
        // apply discount on basePrice
        return getDiscountedPrice(basePrice, pricingOption, discount)
      }
    } else {
      // offer does not have discount

      // if pricing option is custom price return offer price
      if (pricingOption === 'custom_price') {
        return offerPrice
      }
      // else return base price
      else {
        return basePrice
      }
    }
  }

  const deleteOffer = async (id, handler) => {
    setIsRemoving(true)
    try {
      // Delete offer
      const result = await removeOffer(id)
      handler()
      return result
    } catch (error) {
      throw error // Re-throw to be handled by caller
    } finally {
      setIsRemoving(false)
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          data-testid="offers-wrapper-section"
          className="max-width-wrapper event-form"
        >
          {!location.pathname.includes('/create') &&
          !location.pathname.includes('/edit') ? (
            <section>
              <header className="section-header section-heading--flex section-heading section-heading--secondary">
                <h1>Offers</h1>
                {offers?.length > 0 && (
                  <Button
                    size="lg"
                    disabled={
                      hasEventEnded || !standardAdmissionOfferHasInventory
                    }
                    onClick={onClick}
                    className="ms-auto"
                  >
                    Create offer
                  </Button>
                )}
              </header>
              {offers?.length > 0 ? (
                <Stack gap={4}>
                  <Offers
                    isEventPublished={isEventPublished}
                    isEventOnsale={isEventOnsale}
                    timezone={event?.timezone}
                    offers={offers}
                    openInventory={openInventory}
                    holds={event?.inventory?.holds}
                    kills={event?.inventory?.kills}
                    handleClick={handleClick}
                    getOfferPrice={getOfferPrice}
                    tickets={event?.tickets}
                    getOfferStatus={getOfferStatus}
                    getSeatCount={getSeatCount}
                    getAvailableSeatCount={getAvailableSeatCount}
                  />
                  <UpdatingAlert
                    isEventPublished={isEventPublished}
                    eventUUID={eventId}
                  />
                </Stack>
              ) : (
                <Card body>
                  <EmptyContainer style="center lg">
                    <p>
                      You must finish scaling your tickes in order to create an
                      offer.
                    </p>
                    <Button onClick={() => navigate('../seatmap')}>
                      Go to Seat Map
                    </Button>
                  </EmptyContainer>
                </Card>
              )}
            </section>
          ) : (
            <CreateOfferWrapper
              isEventPublishing={isEventPublishing}
              isEventSoldout={isEventSoldout}
              hasEventEnded={hasEventEnded}
              eventId={eventId}
              event={event}
              id={id}
              isSpecialtyOffer={
                offer?.isConnectedOffer || offerType === 'specialty'
              }
              offers={offers}
              currentOffer={offer}
              openInventory={openInventory}
              standardAdmissionOfferHasInventory={
                standardAdmissionOfferHasInventory
              }
              setIsChangesMade={setIsChangesMade}
              getDiscountedPrice={getDiscountedPrice}
              getAvailabilityPeriods={getAvailabilityPeriods}
              getOfferPrice={getOfferPrice}
              getOfferStatus={getOfferStatus}
              getSeatCount={getSeatCount}
              removeOffer={deleteOffer}
              isRemoving={isRemoving}
            />
          )}
        </section>
      </div>

      <CreateOfferModal
        show={show}
        offerType={offerType}
        handleClose={handleClose}
        handleCreate={handleCreate}
      />
    </>
  )
}
